import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";

/**
 * PlanB Jobs – About Us page
 * Route: /about-us
 * Styled to match the PlanB Jobs app: purple → pink gradient, white rounded
 * cards, lavender background, Poppins, mint icon tiles, lime accent from footer.
 */

const SEO = {
  title: "About PlanB Jobs – AI-Powered Recruitment Platform in India",
  description:
    "Learn about PlanB Jobs, an Indian job search and recruitment platform helping job seekers discover opportunities and employers connect with talent.",
};

/* ---------- icons (inline SVG, no extra dependency) ---------- */
const ICONS = {
  rupee: "M6 4h12M6 9h12M9 4c5 0 7 2 7 5s-2 5-7 5H7l9 7",
  search: "M11 4a7 7 0 100 14 7 7 0 000-14zm9 16l-4-4",
  shield: "M12 3l8 3v6c0 4.5-3.2 8-8 9-4.8-1-8-4.5-8-9V6l8-3zm-3.5 9l2.5 2.5L16 9.5",
  spark: "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3zm7 12l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15z",
  layers: "M12 3l9 5-9 5-9-5 9-5zm-9 9l9 5 9-5M3 16l9 5 9-5",
  building: "M5 21V4h9v17M14 9h5v12M3 21h18M8 8h2M8 12h2M8 16h2",
  target: "M12 3a9 9 0 100 18 9 9 0 000-18zm0 5a4 4 0 100 8 4 4 0 000-8zm0 3a1 1 0 100 2 1 1 0 000-2z",
  network: "M12 4a2 2 0 100 4 2 2 0 000-4zM5 16a2 2 0 100 4 2 2 0 000-4zm14 0a2 2 0 100 4 2 2 0 000-4zM12 8v4m0 0l-6 4m6-4l6 4",
  user: "M12 4a4 4 0 100 8 4 4 0 000-8zm-7 16c0-3.5 3-6 7-6s7 2.5 7 6",
  briefcase: "M4 8h16v11H4V8zm5 0V5h6v3M4 13h16",
};

function Icon({ name, size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={ICONS[name]} />
    </svg>
  );
}

const DIFFERENCES = [
  { icon: "rupee", title: "Affordable recruitment solutions" },
  { icon: "search", title: "Candidate-focused job discovery" },
  { icon: "shield", title: "Verified employer processes" },
  { icon: "spark", title: "AI-powered recruitment direction" },
  { icon: "layers", title: "Opportunities across multiple industries" },
  { icon: "building", title: "Technology built for businesses of every size" },
];

const AUDIENCES = [
  {
    icon: "user",
    heading: "Looking for work",
    people: ["Job seekers", "Freshers", "Experienced professionals"],
  },
  {
    icon: "briefcase",
    heading: "Looking to hire",
    people: [
      "Startups",
      "SMEs",
      "Enterprises",
      "HR teams",
      "Recruitment professionals",
    ],
  },
];

const css = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

.pb-about {
  --purple: #7c3aed;
  --pink: #db2777;
  --grad: linear-gradient(90deg, #7c3aed 0%, #db2777 100%);
  --lav: #faf5ff;
  --ink: #1e1b2e;
  --muted: #6b7280;
  --mint: #d1fae5;
  --green: #059669;
  --navy: #17142a;
  --lime: #c7f464;
  font-family: 'Poppins', system-ui, sans-serif;
  color: var(--ink);
  background: var(--lav);
  line-height: 1.7;
  overflow-x: hidden;
}
.pb-about *, .pb-about *::before, .pb-about *::after { box-sizing: border-box; }
.pb-about h1, .pb-about h2, .pb-about h3 { margin: 0; line-height: 1.15; }
.pb-about p { margin: 0; }
.pb-wrap { max-width: 1120px; margin: 0 auto; padding: 0 24px; }

.pb-gradtext {
  background: var(--grad);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* Buttons */
.pb-btn {
  display: inline-block;
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.pb-btn:hover { transform: translateY(-2px); }
.pb-btn:focus-visible { outline: 3px solid var(--lime); outline-offset: 3px; }
.pb-btn-white { background: #fff; color: var(--purple); box-shadow: 0 8px 24px rgba(30, 27, 46, 0.18); }
.pb-btn-ghost { border: 2px solid rgba(255, 255, 255, 0.7); color: #fff; }
.pb-btn-grad { background: var(--grad); color: #fff; box-shadow: 0 8px 24px rgba(124, 58, 237, 0.3); }

/* Hero */
.pb-hero {
  position: relative;
  background: var(--grad);
  color: #fff;
  padding: 80px 0 170px;
  border-radius: 0 0 48px 48px;
  overflow: hidden;
}
.pb-hero::before, .pb-hero::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}
.pb-hero::before { width: 420px; height: 420px; top: -160px; right: -100px; }
.pb-hero::after { width: 260px; height: 260px; bottom: -120px; left: -80px; }
.pb-hero-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  gap: 48px;
  align-items: center;
}
.pb-hero h1 { font-size: clamp(2.1rem, 4.6vw, 3.4rem); font-weight: 800; letter-spacing: -0.02em; }
.pb-hero-intro { margin-top: 24px; font-size: 1.05rem; color: rgba(255, 255, 255, 0.92); max-width: 52ch; }
.pb-hero-goal {
  margin-top: 20px;
  display: inline-block;
  padding: 12px 18px;
  background: rgba(255, 255, 255, 0.16);
  border-left: 4px solid var(--lime);
  border-radius: 0 12px 12px 0;
  font-weight: 600;
  max-width: 52ch;
}
.pb-hero-actions { margin-top: 32px; display: flex; flex-wrap: wrap; gap: 14px; }

/* Hero visual */
.pb-visual { display: grid; gap: 14px; justify-items: stretch; }
.pb-vcard {
  background: #fff;
  color: var(--ink);
  border-radius: 20px;
  padding: 20px 22px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 18px 40px rgba(30, 27, 46, 0.25);
}
.pb-vcard:nth-child(3) { margin-left: 36px; }
.pb-vcard strong { display: block; font-size: 1.05rem; }
.pb-vcard span { font-size: 0.88rem; color: var(--muted); }
.pb-link {
  justify-self: center;
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--navy);
  color: #fff;
  font-weight: 700;
  padding: 10px 22px;
  border-radius: 999px;
  border: 2px solid var(--lime);
}
.pb-link b { color: var(--lime); }

/* Icon tile (matches profile-strength mint tiles) */
.pb-tile {
  flex: none;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: var(--mint);
  color: var(--green);
  display: grid;
  place-items: center;
}

/* Mission & vision */
.pb-mv { position: relative; z-index: 2; margin-top: -100px; }
.pb-mv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.pb-card {
  background: #fff;
  border-radius: 24px;
  padding: 36px;
  box-shadow: 0 12px 40px rgba(124, 58, 237, 0.12);
  border-top: 6px solid transparent;
  border-image: var(--grad) 1;
  border-image-slice: 1;
}
.pb-card h2 { margin-top: 18px; font-size: 1.6rem; font-weight: 700; }
.pb-card p { margin-top: 10px; color: var(--muted); font-size: 1.02rem; }

/* Sections */
.pb-section { padding: 96px 0 0; }
.pb-head { text-align: center; max-width: 620px; margin: 0 auto 48px; }
.pb-head h2 { font-size: clamp(1.8rem, 3.6vw, 2.5rem); font-weight: 800; letter-spacing: -0.01em; }

/* Differences */
.pb-diff-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.pb-diff-item {
  background: #fff;
  border-radius: 20px;
  padding: 28px 26px;
  box-shadow: 0 6px 24px rgba(124, 58, 237, 0.08);
  display: flex;
  flex-direction: column;
  gap: 18px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.pb-diff-item:hover { transform: translateY(-4px); box-shadow: 0 14px 34px rgba(124, 58, 237, 0.16); }
.pb-diff-item h3 { font-size: 1.1rem; font-weight: 600; }
.pb-bar { height: 4px; border-radius: 4px; background: var(--grad); width: 56px; }

/* Who we serve */
.pb-serve { margin-top: 96px; background: var(--navy); color: #fff; padding: 88px 0; border-radius: 48px; }
.pb-serve .pb-head h2 { color: #fff; }
.pb-serve-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.pb-group {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 24px;
  padding: 32px;
}
.pb-group-top { display: flex; align-items: center; gap: 14px; }
.pb-group h3 { font-size: 1.3rem; font-weight: 700; }
.pb-group .pb-tile { background: var(--lime); color: var(--navy); }
.pb-chips { margin: 24px 0 0; padding: 0; list-style: none; display: flex; flex-wrap: wrap; gap: 10px; }
.pb-chips li {
  padding: 8px 18px;
  border-radius: 999px;
  background: var(--grad);
  font-weight: 500;
  font-size: 0.95rem;
}

/* CTA */
.pb-cta { padding: 96px 0; }
.pb-cta-box {
  background: var(--grad);
  color: #fff;
  border-radius: 32px;
  padding: 64px 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(124, 58, 237, 0.3);
}
.pb-cta-box::before {
  content: "";
  position: absolute;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  top: -140px;
  left: -90px;
}
.pb-cta-box > * { position: relative; }
.pb-cta-box h2 { font-size: clamp(1.6rem, 3.6vw, 2.5rem); font-weight: 800; }
.pb-cta-box h2 + h2 { margin-top: 6px; color: var(--lime); }
.pb-cta-box p { margin-top: 18px; font-size: 1.1rem; color: rgba(255, 255, 255, 0.92); }
.pb-cta-actions { margin-top: 32px; display: flex; flex-wrap: wrap; justify-content: center; gap: 14px; }

@media (max-width: 860px) {
  .pb-hero { padding: 56px 0 150px; border-radius: 0 0 32px 32px; }
  .pb-hero-grid, .pb-mv-grid, .pb-serve-grid { grid-template-columns: 1fr; }
  .pb-diff-grid { grid-template-columns: 1fr 1fr; }
  .pb-vcard:nth-child(3) { margin-left: 0; }
  .pb-section { padding-top: 72px; }
  .pb-serve { margin-top: 72px; padding: 64px 0; border-radius: 32px; }
  .pb-cta { padding: 72px 0; }
}
@media (max-width: 560px) {
  .pb-diff-grid { grid-template-columns: 1fr; }
  .pb-card, .pb-group { padding: 26px; }
  .pb-cta-box { padding: 48px 24px; }
}
@media (prefers-reduced-motion: reduce) {
  .pb-btn, .pb-diff-item { transition: none; }
  .pb-btn:hover, .pb-diff-item:hover { transform: none; }
}
`;

function setMeta(name, content) {
  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

export default function AboutUs() {
  useEffect(() => {
    document.title = SEO.title;
    setMeta("description", SEO.description);
  }, []);

  return (
    <>
      <Navbar />
      <main className="pb-about">
        <style>{css}</style>

        {/* Hero */}
        <section className="pb-hero">
          <div className="pb-wrap pb-hero-grid">
            <div>
              <h1>We’re here to make hiring and job searching simpler.</h1>
              <p className="pb-hero-intro">
                The world of work is changing. People want better
                opportunities, and businesses want the right talent without
                complicated recruitment processes. PlanB Jobs was created to
                bring job discovery and recruitment together in one accessible
                platform.
              </p>
              <p className="pb-hero-goal">
                Our goal is simple: make opportunities easier to discover and
                recruitment easier to manage.
              </p>
              <div className="pb-hero-actions">
                <Link className="pb-btn pb-btn-white" to="/jobs">
                  Find jobs
                </Link>
                <Link className="pb-btn pb-btn-ghost" to="/register">
                  Start hiring
                </Link>
              </div>
            </div>

            <div className="pb-visual" aria-hidden="true">
              <div className="pb-vcard">
                <span className="pb-tile"><Icon name="user" /></span>
                <div>
                  <strong>Job seekers</strong>
                  <span>Discover opportunities</span>
                </div>
              </div>
              <div className="pb-link"><b>PlanB</b>Jobs</div>
              <div className="pb-vcard">
                <span className="pb-tile"><Icon name="briefcase" /></span>
                <div>
                  <strong>Employers</strong>
                  <span>Manage recruitment</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & vision */}
        <section className="pb-mv" aria-label="Mission and vision">
          <div className="pb-wrap pb-mv-grid">
            <div className="pb-card">
              <span className="pb-tile"><Icon name="target" /></span>
              <h2>Our mission</h2>
              <p>
                To make employment opportunities more accessible and
                recruitment more efficient through technology.
              </p>
            </div>
            <div className="pb-card">
              <span className="pb-tile"><Icon name="network" /></span>
              <h2>Our vision</h2>
              <p>
                To build a trusted recruitment ecosystem connecting job
                seekers, employers and recruitment professionals across India.
              </p>
            </div>
          </div>
        </section>

        {/* What makes us different */}
        <section className="pb-section">
          <div className="pb-wrap">
            <div className="pb-head">
              <h2>
                What makes us <span className="pb-gradtext">different?</span>
              </h2>
            </div>
            <div className="pb-diff-grid">
              {DIFFERENCES.map((d) => (
                <div className="pb-diff-item" key={d.title}>
                  <span className="pb-tile"><Icon name={d.icon} /></span>
                  <h3>{d.title}</h3>
                  <span className="pb-bar" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who we serve */}
        <section className="pb-serve">
          <div className="pb-wrap">
            <div className="pb-head">
              <h2>Who we serve</h2>
            </div>
            <div className="pb-serve-grid">
              {AUDIENCES.map((g) => (
                <div className="pb-group" key={g.heading}>
                  <div className="pb-group-top">
                    <span className="pb-tile"><Icon name={g.icon} /></span>
                    <h3>{g.heading}</h3>
                  </div>
                  <ul className="pb-chips">
                    {g.people.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="pb-cta">
          <div className="pb-wrap">
            <div className="pb-cta-box">
              <h2>Your next opportunity matters.</h2>
              <h2>Your next great hire matters.</h2>
              <p>Let’s make both happen.</p>
              <div className="pb-cta-actions">
                <Link className="pb-btn pb-btn-white" to="/jobs">
                  Find jobs
                </Link>
                <Link className="pb-btn pb-btn-ghost" to="/register">
                  Start hiring
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}